import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import enTranslations from "./locales/en.json";

const LANGUAGE_STORAGE_KEY = "language";
const DEFAULT_LANGUAGE = "en";
const SUPPORTED_LANGUAGES = ["en", "uz", "ru"] as const;

type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];
type LocaleModule = { default: Record<string, unknown> };

let initPromise: Promise<typeof i18n> | null = null;

function normalizeLanguage(language?: string | null): SupportedLanguage {
  const baseLanguage = language?.toLowerCase().split("-")[0];
  return SUPPORTED_LANGUAGES.includes(baseLanguage as SupportedLanguage)
    ? (baseLanguage as SupportedLanguage)
    : DEFAULT_LANGUAGE;
}

function getPreferredLanguage(): SupportedLanguage {
  if (typeof window === "undefined") {
    return DEFAULT_LANGUAGE;
  }

  return normalizeLanguage(
    window.localStorage.getItem(LANGUAGE_STORAGE_KEY) || navigator.language,
  );
}

async function loadLocale(language: SupportedLanguage) {
  switch (language) {
    case "ru":
      return (await import("./locales/ru.json")) as LocaleModule;
    case "uz":
      return (await import("./locales/uz.json")) as LocaleModule;
    case "en":
    default:
      return { default: enTranslations as Record<string, unknown> };
  }
}

function persistLanguage(language: SupportedLanguage) {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
}

async function ensureLocale(language: string) {
  const normalizedLanguage = normalizeLanguage(language);

  if (!i18n.hasResourceBundle(normalizedLanguage, "translation")) {
    const locale = await loadLocale(normalizedLanguage);
    i18n.addResourceBundle(
      normalizedLanguage,
      "translation",
      locale.default,
      true,
      true,
    );
  }

  return normalizedLanguage;
}

export async function changeAppLanguage(language: string) {
  const normalizedLanguage = await ensureLocale(language);
  persistLanguage(normalizedLanguage);
  await i18n.changeLanguage(normalizedLanguage);
  return normalizedLanguage;
}

export async function initI18n() {
  if (initPromise) {
    return initPromise;
  }

  initPromise = (async () => {
    const preferredLanguage = getPreferredLanguage();

    i18n.use(initReactI18next);

    await i18n.init({
      lng: DEFAULT_LANGUAGE,
      fallbackLng: DEFAULT_LANGUAGE,
      initImmediate: false,
      supportedLngs: [...SUPPORTED_LANGUAGES],
      nonExplicitSupportedLngs: true,
      partialBundledLanguages: true,
      resources: {
        [DEFAULT_LANGUAGE]: { translation: enTranslations },
      },
      interpolation: {
        escapeValue: false,
      },
    });

    if (preferredLanguage !== DEFAULT_LANGUAGE) {
      void changeAppLanguage(preferredLanguage);
    }

    return i18n;
  })();

  return initPromise;
}

export default i18n;
